import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';

type NoticeType = 'success' | 'error';

interface Notice {
  id: number;
  type: NoticeType;
  message: string;
  // false にすると退場アニメーションが始まり、終わったら一覧から取り除く
  show: boolean;
}

interface ManagementNoticeContextValue {
  notifySuccess: (message: string) => void;
  notifyError: (message: string) => void;
}

const ManagementNoticeContext = createContext<ManagementNoticeContextValue | undefined>(undefined);

// 管理画面で更新・削除・作成系の API を叩いた結果を通知するポップアップ。
// 子コンポーネントは useManagementNotice() で notifySuccess / notifyError を呼ぶ
export const ManagementNoticeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const nextId = useRef(0);

  const hideNotice = useCallback((id: number) => {
    setNotices(prev => prev.map(notice => notice.id === id ? { ...notice, show: false } : notice));
  }, []);

  const removeNotice = useCallback((id: number) => {
    setNotices(prev => prev.filter(notice => notice.id !== id));
  }, []);

  const addNotice = useCallback((type: NoticeType, message: string) => {
    const id = nextId.current++;
    setNotices(prev => [...prev, { id, type, message, show: true }]);
  }, []);

  const notifySuccess = useCallback((message: string) => addNotice('success', message), [addNotice]);
  const notifyError = useCallback((message: string) => addNotice('error', message), [addNotice]);

  return (
    <ManagementNoticeContext.Provider value={{ notifySuccess, notifyError }}>
      {children}
      <ToastContainer position="bottom-end" className="p-3 position-fixed" style={{ zIndex: 2000 }}>
        {notices.map(notice => (
          <Toast
            key={notice.id}
            className="managementNotice"
            bg={notice.type === 'success' ? 'success' : 'danger'}
            show={notice.show}
            onClose={() => hideNotice(notice.id)}
            onExited={() => removeNotice(notice.id)}
            // エラーは読み逃さないよう成功時より長めに表示する
            delay={notice.type === 'success' ? 3000 : 6000}
            autohide
          >
            <Toast.Header>
              <strong className="me-auto">{notice.type === 'success' ? '更新成功' : 'エラー'}</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{notice.message}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ManagementNoticeContext.Provider>
  );
};

export const useManagementNotice = (): ManagementNoticeContextValue => {
  const context = useContext(ManagementNoticeContext);
  if (context === undefined) {
    throw new Error('useManagementNotice は ManagementNoticeProvider の内側で使ってください');
  }
  return context;
};
